import { Company } from 'module/api/company/types';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { AccountPeopleProfile } from 'api/account-people/types';
import moment from 'moment';
import { useGetDocumentQuery } from 'queries/pages';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { WidthTransition } from 'styles/common';
import { UserPageDocument } from './document';
import { getIcon, Sections } from '../../content-navigator';
import { ContentSection, EmptyStateForSection } from '../../content-section';

const Wrapper = styled.div<{ $isFullWidth?: boolean }>`
  ${WidthTransition};
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? css`
          max-width: 100%;
        `
      : css`
          max-width: 560px;
        `};
`;

interface Props {
  userProfile: AccountPeopleProfile;
  company: Company;
  isFullWidth?: boolean;
}
export const UserPageSection = ({ userProfile, isFullWidth }: Props) => {
  const documentSourceMappingModel = userProfile.documentSourceMappingModel;
  const userDocumentId = documentSourceMappingModel?.documentId;
  const { data: userDocument, isLoading: isUserDocumentLoading } =
    useGetDocumentQuery(userDocumentId ?? -1, {
      enabled: userDocumentId != null,
      retry: false,
    });
  const isEmpty = userDocumentId == null;
  const description = useMemo(() => {
    if (documentSourceMappingModel == null) {
      return undefined;
    }
    if (userDocument != null) {
      return `${humanise(moment(userDocument.metadata.createdAt))} - ${humanise(moment(userDocument.metadata.updatedAt))}`;
    }

    return `${humanise(moment(documentSourceMappingModel.createdAt))} - ${humanise(moment(documentSourceMappingModel.updatedAt))}`;
  }, [documentSourceMappingModel, userDocument]);

  return (
    <ContentSection
      icon={<FontAwesomeIcon icon={getIcon(Sections.UserPage)} size={16} />}
      title={
        <FormattedMessage
          id="account.people.profile.sections.user_page.title"
          values={{
            name: userProfile.user.firstName ?? userProfile.user.lastName,
          }}
        />
      }
      description={description}
      sectionKey={Sections.UserPage}
      loading={isUserDocumentLoading}
    >
      {userDocument != null && !isEmpty ? (
        <Wrapper $isFullWidth={isFullWidth}>
          <UserPageDocument document={userDocument} />
        </Wrapper>
      ) : (
        <EmptyStateForSection
          sectionKey={Sections.UserPage}
          message={
            <FormattedMessage id="account.people.profile.sections.user_page.empty" />
          }
        />
      )}
    </ContentSection>
  );
};
